<template>
  <div dir="rtl" class="min-h-screen overflow-auto pb-28">
    <div class="max-w-7xl mx-auto p-2 md:p-0">
      
      <!-- تغییرات: جایگزینی کارت‌های موجودی با کارت‌های آمار قیمت -->
      <div class="grid grid-cols-2 gap-3 mb-6">
        <!-- High Price Card -->
        <div class="rounded-2xl bg-gradient-to-br from-[#1a2220] to-[#12181b] ring-1 ring-[#334D44]/60 px-4 py-4 flex items-center justify-between text-white relative overflow-hidden group">
          <div class="absolute -left-4 -bottom-4 w-24 h-24 bg-emerald-500/10 rounded-full blur-2xl group-hover:bg-emerald-500/20 transition duration-500"></div>
          <div class="relative z-10 w-full">
            <div class="text-xs text-white/60 mb-1.5 flex items-center gap-1.5">
               <span class="p-1 rounded-md bg-emerald-500/10 text-emerald-400">
                 <svg class="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6"/></svg>
               </span>
               بیشترین قیمت امروز
            </div>
            <div class="text-lg md:text-2xl font-black text-emerald-400 tracking-tight flex items-baseline gap-1">
              {{ formatMoney(dayStats.high) }} 
              <span class="text-[10px] md:text-xs font-medium text-white/40">تومان</span>
            </div>
          </div>
        </div>

        <!-- Low Price Card -->
        <div class="rounded-2xl bg-gradient-to-br from-[#1a2220] to-[#12181b] ring-1 ring-[#334D44]/60 px-4 py-4 flex items-center justify-between text-white relative overflow-hidden group">
          <div class="absolute -left-4 -bottom-4 w-24 h-24 bg-rose-500/10 rounded-full blur-2xl group-hover:bg-rose-500/20 transition duration-500"></div>
          <div class="relative z-10 w-full">
            <div class="text-xs text-white/60 mb-1.5 flex items-center gap-1.5">
               <span class="p-1 rounded-md bg-rose-500/10 text-rose-400">
                 <svg class="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13 17h8m0 0V9m0 8l-8-8-4 4-6-6"/></svg>
               </span>
               کمترین قیمت امروز
            </div>
            <div class="text-lg md:text-2xl font-black text-rose-400 tracking-tight flex items-baseline gap-1">
              {{ formatMoney(dayStats.low) }} 
              <span class="text-[10px] md:text-xs font-medium text-white/40">تومان</span>
            </div>
          </div>
        </div>
      </div>

      <div class="grid lg:grid-cols-3 gap-6">
        <!-- Main Price Board -->
        <div class="rounded-3xl bg-gradient-to-b from-[#1a2220] to-[#12181b] ring-1 ring-[#334D44]/60 p-6 text-white relative overflow-hidden shadow-2xl">
          <div class="relative z-10 flex flex-col h-full justify-between">
            <div>
              <div class="flex items-center gap-2 mb-6">
                <span class="relative flex h-3 w-3">
                  <span class="animate-ping absolute inline-flex h-full w-full rounded-full bg-[#b9f1cf] opacity-75"></span>
                  <span class="relative inline-flex rounded-full h-3 w-3 bg-[#b9f1cf]"></span>
                </span>
                <h2 class="font-bold text-lg">تابلو قیمت لحظه‌ای</h2>
              </div>

              <div class="mb-8 text-center relative">
                <div class="absolute inset-0 bg-[#b9f1cf]/5 blur-3xl rounded-full pointer-events-none"></div>
                <div class="text-sm text-white/60 mb-2">قیمت هر گرم طلای ۱۸ عیار</div>
                <div class="text-5xl md:text-6xl font-black text-[#b9f1cf] tracking-tighter drop-shadow-[0_0_15px_rgba(185,241,207,0.3)]">
                  {{ numericGoldPrice ? formatMoney(numericGoldPrice) : '---' }}
                </div>
                <div class="text-sm text-white/40 mt-2">تومان</div>
              </div>

              <div class="space-y-4">
                <div class="flex items-center justify-between p-4 rounded-2xl bg-white/5 ring-1 ring-white/10 backdrop-blur-md">
                  <div class="text-sm text-white/70 flex items-center gap-2">
                    <i class="fas fa-clock text-[#b9f1cf]"></i>
                    زمان آخرین نرخ
                  </div>
                  <div class="text-base font-bold font-mono text-[#b9f1cf] dir-ltr">{{ lastUpdateTime }}</div>
                </div>
              </div>
            </div>
            
            <div class="mt-6 text-center text-[10px] text-white/30 font-light">
              قیمت‌ها بر اساس نرخ بازار تهران محاسبه می‌شود
            </div>
          </div>
        </div>

        <!-- Analytics Chart -->
        <div class="lg:col-span-2 rounded-3xl bg-gradient-to-b from-[#1a2220] to-[#12181b] ring-1 ring-[#334D44]/60 p-6 relative shadow-xl">
          <div class="flex flex-wrap items-center justify-between gap-4 mb-6">
            <h3 class="font-bold text-white text-lg flex items-center gap-2">
              <i class="fas fa-chart-area text-[#b9f1cf]"></i>
              نمودار تحلیلی
            </h3>
            <div class="flex bg-[#0f1417] rounded-xl p-1 ring-1 ring-white/10">
              <button 
                v-for="p in ['1W', '1M', '3M', '6M']" 
                :key="p"
                @click="chartPeriod = p"
                :class="['px-3 py-1.5 text-xs rounded-lg transition-all', chartPeriod === p ? 'bg-[#b9f1cf] text-[#0a1f14] font-bold shadow-md' : 'text-white/60 hover:text-white']">
                {{ p === '1W' ? 'هفتگی' : p === '1M' ? 'ماهانه' : p === '3M' ? '۳ ماهه' : '۶ ماهه' }}
              </button>
            </div>
          </div>
          
          <div class="relative h-[350px] w-full" dir="ltr">
            <div id="mainChart"></div>
          </div>
        </div>
      </div>

      <!-- Shop Section -->
      <div class="mt-12">
        <div class="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
          <div>
            <h2 class="text-2xl font-extrabold text-white flex items-center gap-2">
              <span class="w-1.5 h-6 bg-[#b9f1cf] rounded-full"></span>
              فروشگاه طلا و سکه
            </h2>
          </div>
          <div class="grid grid-cols-1 sm:grid-cols-3 gap-2 w-full sm:w-auto">
            <div class="relative">
              <input v-model.trim="prodSearch" type="search" placeholder="جستجو..." class="w-full sm:w-56 rounded-xl bg-white/5 ring-1 ring-white/10 focus:ring-2 focus:ring-[#b9f1cf] px-9 py-2.5 text-sm text-white placeholder:text-white/40 transition"/>
              <svg class="absolute right-3 top-1/2 -translate-y-1/2 h-4 w-4 text-white/50" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"/></svg>
            </div>
            <select v-model="prodSort" class="rounded-xl text-white bg-white/5 ring-1 ring-white/10 focus:ring-2 focus:ring-[#b9f1cf] px-3 py-2.5 text-sm outline-none">
              <option value="new" class="bg-[#1a2220]">جدیدترین</option>
              <option value="priceAsc" class="bg-[#1a2220]">ارزان‌ترین</option>
              <option value="priceDesc" class="bg-[#1a2220]">گران‌ترین</option>
            </select>
            <select v-model.number="prodPerPage" class="rounded-xl text-white bg-white/5 ring-1 ring-white/10 focus:ring-2 focus:ring-[#b9f1cf] px-3 py-2.5 text-sm outline-none">
              <option :value="8" class="bg-[#1a2220]">۸ عدد</option>
              <option :value="12" class="bg-[#1a2220]">۱۲ عدد</option>
            </select>
          </div>
        </div>

        <div class="flex flex-wrap items-center gap-2 mb-6">
          <button v-for="c in categoryChips" :key="c.value" @click="toggleShopCat(c.value)"
            :class="['px-4 py-2 rounded-full text-sm font-medium ring-1 transition-all',
                     shopCats.includes(c.value) ? 'bg-[#b9f1cf] text-[#0a1f14] ring-[#b9f1cf] shadow-[0_0_10px_rgba(185,241,207,0.2)]' : 'bg-[#1e2623] text-white/70 ring-[#334D44]/60 hover:bg-[#25302c] hover:text-white']">
            {{ c.label }}
          </button>
        </div>

        <div v-if="prodLoading" class="grid grid-cols-2 md:grid-cols-4 gap-6 animate-pulse">
           <div v-for="i in 4" :key="i" class="h-80 rounded-2xl bg-white/5 ring-1 ring-white/10"></div>
        </div>

        <div v-else>
          <div v-if="paginatedProducts.length" class="grid gap-6 grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
            <div v-for="p in paginatedProducts" :key="p._id" class="group relative rounded-2xl bg-[#1e2623] ring-1 ring-[#334D44]/40 p-3 transition-all hover:-translate-y-1 hover:shadow-xl hover:ring-[#b9f1cf]/50">
              <div class="relative overflow-hidden rounded-xl bg-[#151b19] aspect-[4/3] mb-3 group-hover:bg-[#0f1417] transition-colors">
                <img :src="p.img || placeholder" class="h-full w-full object-contain p-2 transition-transform duration-500 group-hover:scale-110"/>
                <div class="absolute top-2 right-2 flex gap-1">
                   <span v-if="p.status" class="bg-emerald-500/90 text-white text-[10px] font-bold px-2 py-1 rounded-md backdrop-blur-sm">موجود</span>
                   <span v-else class="bg-rose-500/90 text-white text-[10px] font-bold px-2 py-1 rounded-md backdrop-blur-sm">ناموجود</span>
                </div>
              </div>
              
              <div class="px-1">
                <div class="text-[10px] text-[#b9f1cf] mb-1 opacity-80">{{ p.type || 'سایر' }}</div>
                <h3 class="text-white font-bold text-base truncate">{{ p.title }}</h3>
                
                <div class="flex items-center gap-2 mt-2 text-xs text-white/50">
                   <span class="bg-white/5 px-1.5 py-0.5 rounded">وزن: {{ p.weightGram || 0 }}g</span>
                   <span class="bg-white/5 px-1.5 py-0.5 rounded">عیار: ۱۸</span>
                </div>

                <div class="my-3 h-px bg-gradient-to-r from-transparent via-white/10 to-transparent"></div>

                <div class="flex items-end justify-between">
                  <div>
                    <div class="text-[10px] text-white/40">قیمت نهایی</div>
                    <div class="text-lg font-extrabold text-[#b9f1cf]">{{ productDisplayPrice(p).replace(' تومان', '') }} <span class="text-[10px] font-normal opacity-70">تومان</span></div>
                  </div>
                  <button @click="openDetails(p)" class="h-9 w-9 rounded-xl bg-white/10 flex items-center justify-center text-white hover:bg-[#b9f1cf] hover:text-[#0a1f14] transition shadow-lg">
                    <svg class="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"/><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"/></svg>
                  </button>
                </div>
              </div>
            </div>
          </div>

          <div v-else class="py-20 text-center">
            <div class="inline-flex h-20 w-20 items-center justify-center rounded-full bg-white/5 ring-1 ring-white/10 mb-4">
              <svg class="h-10 w-10 text-white/30" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="1.5" d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10"/></svg>
            </div>
            <p class="text-white/50">محصولی با این مشخصات یافت نشد.</p>
          </div>

          <div class="mt-10 flex justify-center gap-2" v-if="prodTotalPages > 1">
            <button v-for="pg in prodPages" :key="pg" @click="changeProdPage(pg)"
              :class="['h-9 w-9 rounded-lg flex items-center justify-center text-sm transition',
                pg===prodPage ? 'bg-[#b9f1cf] text-[#0a1f14] font-bold shadow-lg shadow-[#b9f1cf]/20' : 'bg-[#1e2623] ring-1 ring-[#334D44]/60 text-white hover:bg-white/10']">
              {{ pg }}
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted, onBeforeUnmount, watch } from 'vue'
import axios from 'axios'
import Swal from 'sweetalert2'
import ApexCharts from 'apexcharts'
import moment from 'moment-jalaali'
import { io } from 'socket.io-client'

const WS_URL = process.env.VUE_APP_API_WS || ''
const url = process.env.VUE_APP_API_URL || ''

const goldPrice = ref(null)
const dayStats = ref({ high: 0, low: 0 })
const lastUpdateTime = ref(moment().format('HH:mm:ss'))
let socket = null

const chartPeriod = ref('1M')
const chartDataFull = ref([])
let chartInstance = null

const item = ref(null)
const buyFeePercent = ref(1.5)

async function fetchSetting() {
  try {
    if (!url) return
    const res = await axios.get(`${url}/admin/setting`)
    item.value = res.data?.data ?? null
    if (item.value && item.value.feebuy != null) buyFeePercent.value = Number(item.value.feebuy)
  } catch {
    console.log("error fetching settings");
  }
}

function openSocket() {
  try { 
    socket = io(WS_URL, { transports: ['websocket'], withCredentials: true }) 
    
    const handlePriceData = (data) => {
        if (data && data.user && data.user.gold18) {
            const p = Number(data.user.gold18.buy) || 0
            
            if (p > 0) {
                goldPrice.value = p
                lastUpdateTime.value = moment().format('HH:mm:ss')

                // بروزرسانی آنی برای high/low اگر قیمت جدید از رنج روز خارج شد
                if (dayStats.value.high === 0 || p > dayStats.value.high) dayStats.value.high = p
                if (dayStats.value.low === 0 || p < dayStats.value.low) dayStats.value.low = p
            }
        }
    }

    socket.on('prices:init', handlePriceData)
    socket.on('prices:update', handlePriceData)

  } catch (err) { 
    console.error("Socket Error:", err)
  }
}

onBeforeUnmount(() => { 
    try { if (socket) socket.disconnect() } catch {
      console.log("error");
      
    } 
})

const numericGoldPrice = computed(() => Number(goldPrice.value) || 0)

function formatMoney(n){ if(!isFinite(n)) return '۰'; return new Intl.NumberFormat('fa-IR').format(Math.round(Number(n))) }

async function fetchChartData() {
  try {
    const { data } = await axios.get(`${url}/api/gold/history`)
    
    // دیتای کامل برای نمودار
    chartDataFull.value = (data || []).map(i => ({ x: new Date(i.date).getTime(), y: i.price })).sort((a,b) => a.x - b.x)
    
    // --- محاسبه Min/Max امروز از دیتابیس ---
    const todayStr = new Date().toDateString()
    
    // فیلتر کردن رکوردهایی که تاریخشان برابر با امروز است
    const todayRecords = (data || []).filter(item => {
        return new Date(item.date).toDateString() === todayStr
    })

    if (todayRecords.length > 0) {
        // استخراج قیمت‌ها
        const prices = todayRecords.map(i => i.price)
        dayStats.value.high = Math.max(...prices)
        dayStats.value.low = Math.min(...prices)
    } else {
        // اگر هنوز رکوردی برای امروز در دیتابیس نیست (مثلاً اول صبح)
        // می‌توانیم از آخرین قیمت سوکت استفاده کنیم یا صفر بگذاریم
        // در اینجا اگر صفر باشد، با اولین دیتای سوکت (در openSocket) پر می‌شود
    }

    updateChart()
  } catch {
    console.log("error chart");
  }
}

function updateChart() {
  if (!chartDataFull.value.length) return
  const now = new Date()
  let limitDate = new Date()
  
  if (chartPeriod.value === '1W') limitDate.setDate(now.getDate() - 7)
  else if (chartPeriod.value === '1M') limitDate.setMonth(now.getMonth() - 1)
  else if (chartPeriod.value === '3M') limitDate.setMonth(now.getMonth() - 3)
  else if (chartPeriod.value === '6M') limitDate.setMonth(now.getMonth() - 6)

  const filtered = chartDataFull.value.filter(d => d.x >= limitDate.getTime())
  
  const options = {
    series: [{ name: 'قیمت طلا', data: filtered }],
    chart: { type: 'area', height: 350, fontFamily: 'inherit', background: 'transparent', toolbar: { show: false }, zoom: { enabled: false } },
    colors: ['#b9f1cf'],
    fill: { type: 'gradient', gradient: { shadeIntensity: 1, opacityFrom: 0.4, opacityTo: 0.05, stops: [0, 100] } },
    dataLabels: { enabled: false },
    stroke: { curve: 'smooth', width: 2 },
    grid: { borderColor: 'rgba(255,255,255,0.05)', strokeDashArray: 4 },
    xaxis: { type: 'datetime', labels: { style: { colors: '#9ca3af', fontFamily: 'inherit' }, formatter: (val) => moment(val).format('jD jMMMM') }, axisBorder: { show: false }, axisTicks: { show: false } },
    yaxis: { labels: { style: { colors: '#9ca3af', fontFamily: 'inherit' }, formatter: (val) => new Intl.NumberFormat('en-US', { notation: "compact" }).format(val) } },
    tooltip: { theme: 'dark', x: { formatter: (val) => moment(val).format('jYYYY/jMM/jDD HH:mm') }, y: { formatter: (val) => formatMoney(val) + ' تومان' }, style: { fontFamily: 'inherit' } }
  }

  const el = document.querySelector('#mainChart')
  if (el) {
    if (chartInstance) chartInstance.destroy()
    chartInstance = new ApexCharts(el, options)
    chartInstance.render()
  }
}

watch(chartPeriod, updateChart)

const placeholder = 'https://placehold.co/400x300/1e2623/FFF?text=Gold+Product'
const products = ref([])
const prodLoading = ref(false)
const prodPage = ref(1)
const prodPerPage = ref(8)
const prodTotalPages = ref(1)
const prodSearch = ref('')
const prodSort = ref('new')
const shopCats = ref([])
const categoryChips = [{ value:'coin', label:'سکه' }, { value:'bar', label:'شمش' }, { value:'jewelry', label:'زیورآلات' }, { value:'set', label:'ست' }]

function toggleShopCat(v){ shopCats.value = shopCats.value.includes(v) ? shopCats.value.filter(x=>x!==v) : [...shopCats.value, v] }

async function fetchProducts() {
  prodLoading.value = true
  try {
    const params = { page: prodPage.value, limit: prodPerPage.value, q: prodSearch.value }
    const res = await axios.get(`${url}/products`, { params })
    const data = res.data?.data || res.data
    if (data?.products && Array.isArray(data.products)) {
      products.value = data.products
      const total = Number(data.total || data.count || products.value.length)
      prodTotalPages.value = Math.max(1, Math.ceil(total / prodPerPage.value))
    } else if (Array.isArray(data)) {
      const start = (prodPage.value - 1) * prodPerPage.value
      products.value = data.slice(start, start + prodPerPage.value)
      prodTotalPages.value = Math.max(1, Math.ceil(data.length / prodPerPage.value))
    } else { products.value = []; prodTotalPages.value = 1 }
  } catch { products.value = [] }
  finally { prodLoading.value = false }
}

const filteredProducts = computed(() => {
  let arr = products.value.slice()
  if (shopCats.value.length) arr = arr.filter(p => shopCats.value.includes((p.type || '').toLowerCase()))
  const q = (prodSearch.value||'').trim().toLowerCase()
  if (q) arr = arr.filter(p => `${p.title||''} ${p.type||''}`.toLowerCase().includes(q))
  if (prodSort.value === 'priceAsc') arr.sort((a,b)=>productPrice(a)-productPrice(b))
  else if (prodSort.value === 'priceDesc') arr.sort((a,b)=>productPrice(b)-productPrice(a))
  else arr.sort((a,b)=>new Date(b.createdAt||0)-new Date(a.createdAt||0))
  return arr
})

const paginatedProducts = computed(() => filteredProducts.value)

function changeProdPage(n){ if(n<1) n=1; if(n>prodTotalPages.value) n=prodTotalPages.value; prodPage.value = n }
const prodPages = computed(() => {
  const t = prodTotalPages.value, cur = prodPage.value
  const arr = []
  const start = Math.max(1, cur-2), end = Math.min(t, cur+2)
  for (let i=start;i<=end;i++) arr.push(i)
  return arr
})

function productPrice(p){
  if (!p) return 0
  const explicit = p.price ?? p.pricePerUnit ?? null
  if (explicit != null && !isNaN(Number(explicit))) return Number(explicit)
  const weightGram = Number(p.weightGram ?? (p.weightSot ? Number(p.weightSot)*0.001 : 0)) || 0
  const gp = Number(numericGoldPrice.value) || 0
  if (!weightGram || !gp) return 0
  return Math.round((weightGram * gp) * (1 + (Number(buyFeePercent.value)||0)/100))
}
function productDisplayPrice(p){ const v = productPrice(p); return v ? formatMoney(v) + ' تومان' : '---' }

function openDetails(p){
  Swal.fire({
    title: p.title || 'مشخصات محصول',
    html: `
      <div class="text-right space-y-2">
        <img src="${p.img || placeholder}" class="w-full h-48 object-contain bg-black/20 rounded-xl mb-4"/>
        <div class="flex justify-between text-gray-400 text-sm"><span>دسته‌بندی</span><span class="text-white">${p.type || '-'}</span></div>
        <div class="flex justify-between text-gray-400 text-sm"><span>وزن خالص</span><span class="text-white">${p.weightGram ?? 0} گرم</span></div>
        <div class="border-t border-white/10 my-2"></div>
        <div class="flex justify-between items-center">
            <span class="text-gray-400">قیمت نهایی</span>
            <span class="text-[#b9f1cf] text-xl font-bold">${productDisplayPrice(p)}</span>
        </div>
      </div>
    `,
    showConfirmButton: false,
    showCloseButton: true,
    background: '#1a2220',
    color: '#fff',
    customClass: { popup: 'rounded-2xl border border-[#334D44]' }
  })
}

watch([prodPage, prodPerPage, prodSearch], fetchProducts)

onMounted(() => {
  fetchSetting()
  fetchProducts()
  openSocket()
  fetchChartData()
})
</script>